import { ecId, handBack, inventoryId, itemField, upper, type JanusReaders, type Row } from '../ability/context.js';
import { statusItemId, statusRemovalBefore } from '../statuses/expiry.js';
import { ftTrapArmedStatusRow, ftTrapConsumeArmedState, ftTrapRemoveZones, ftTrapResetIsTimed } from '../port/k4-dispatch/ft_trap_fire.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import { get, type ReadEnv } from '../port/k4-dispatch/shared.js';
import type { DispatchEnv, Ref } from '../port/k4-dispatch/dispatch.js';
import { mountedTrapAbilityIds, trapZoneStatusMatches } from './mounted.js';

/** Shared K4 armed-row gate, using the page's native status reader. */
export function trapArmed(host: Row, statuses: (row: Row) => Row[]): boolean {
  return ftTrapArmedStatusRow(host, browserDependencies({
    creature_statuses: () => statuses(host),
    item_field: (row: unknown) => statusItemId(row as Row),
  }) as unknown as ReadEnv) !== null;
}

export interface TrapLifecycle {
  removals: Array<{ status_id: string; holder_type: 'inventory' | 'encounter' | 'encounter_creature';
    before: { status_target_id: string; status_caster_id: string; status_connected_id: string; status_deleted: number } }>;
  skipped: string[];
}

/** K4 decides the armed/zone connection closure; storage CASes its exact native identities. */
export function planTrapLifecycle(host: Row, encounter: Row, members: ReadonlyMap<string, Row>, ability: Row, read: JanusReaders): TrapLifecycle {
  const plan: TrapLifecycle = { removals: [], skipped: [] };
  const known: Row[] = [];
  const holderTypes = new Map<string, 'inventory' | 'encounter' | 'encounter_creature'>();
  const holderIds = new Map<string, string>();
  const holders = [host, encounter, ...members.values(),
    ...(Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr : [])];
  for (const holder of holders) for (const status of read.statuses(holder as Row)) {
    const id = upper(read.value(status, 'status_id'));
    if (!known.some(row => upper(read.value(row, 'status_id')) === id)) {
      known.push(status);
      holderTypes.set(id, inventoryId(holder as Row) ? 'inventory' : ecId(holder as Row) ? 'encounter_creature' : 'encounter');
      holderIds.set(id, inventoryId(holder as Row) || ecId(holder as Row) || upper(itemField((holder as Row).encounters as Row, 'encounter_id')));
    }
  }
  const hid = inventoryId(host);
  const owned = structuredClone(host);
  const local: Ref = { value: owned }, scene: Ref = { value: encounter };
  const deps = {
    ft_trapped_status_id: () => '78C51D2D-5178-551C-1249-B5DDA4083098',
    creature_statuses: () => read.statuses(owned),
    item_field: (row: unknown, key: unknown) => key === 'item_id' && get(row,'status_id')
      ? statusItemId(row as Row) : itemField(row as Row, String(key)),
    inventory_field: (row: unknown, key: unknown) => key === 'inventory_id' ? inventoryId(row as Row) : read.inventoryField?.(row as Row,String(key)),
    check_uuid: (id: unknown) => /^[0-9A-F-]{36}$/i.test(String(id)),
    ft_trap_mounted_ability_ids: () => mountedTrapAbilityIds(owned,(row,key)=>read.entries(row,key), read.definition),
    query_full_item_arr: (id: unknown) => {
      const def=read.definition(upper(id));if(!def)handBack('trap-lifecycle:mounted-definition-not-held');return def;
    },
    get_inventory_arr: () => owned,
    fetch_statuses: (where: unknown) => known.filter(status => trapZoneStatusMatches(status,
      holderIds.get(upper(read.value(status, 'status_id'))) ?? '',
      upper(get(where, 'status_caster_id')), upper(get(where, 'status_item_id')), read.value)),
    ft_trap_remove_zones: (target: Ref, def: unknown, enc: Ref, campaign: unknown, affected: Ref, roll: unknown) =>
      ftTrapRemoveZones(target,def,enc,campaign,affected,roll,env),
    remove_status_effect_status_id_all_connections_to_all_targets: (_target: Ref, _affected: Ref, raw: unknown) => {
      const id=upper(raw), status=known.find(row=>upper(read.value(row,'status_id'))===id);
      if(!status)handBack('trap-lifecycle:status-row-not-held');
      if(plan.removals.some(row=>row.status_id===id))return;
      const connection=String(read.value(status,'status_connected_id')??'');
      const peers=connection ? known.filter(row=>String(read.value(row,'status_connected_id')??'')===connection) : [status];
      for(const peer of peers){
        const peerId=upper(read.value(peer,'status_id'));
        if(plan.removals.some(row=>row.status_id===peerId))continue;
        const before=statusRemovalBefore(peer,holderIds.get(peerId));
        plan.removals.push({status_id:peerId,holder_type:holderTypes.get(peerId)!,before:{status_target_id:String(before.status_target_id),
          status_caster_id:String(before.status_caster_id??''),status_connected_id:String(before.status_connected_id??''),
          status_deleted:Number(before.status_deleted??0)}});
      }
    },
  } satisfies Partial<DispatchEnv['deps']>;
  const env=browserDependencies(new Proxy(deps,{get:(target,key)=>Reflect.get(target,key)
    ?? (()=>handBack('trap-lifecycle:dependency:'+String(key)))})) as unknown as DispatchEnv;
  const result=ftTrapConsumeArmedState(local,scene,null,{value:[]},'',env);
  if(result.writes.length)handBack('trap-lifecycle:unmapped-write-intent');
  if(ftTrapResetIsTimed(String(read.value(ability,'trap_reset')??'').trim().toLowerCase()))
    plan.skipped.push('trap-lifecycle:timed-rearm:not-ported');
  if(!hid || plan.removals.length===0)handBack('trap-lifecycle:armed-row-not-held');
  return plan;
}
